
import React, { useCallback, useEffect, useMemo } from 'react';
import { DynamicActivityDefinition } from '../types';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Edge,
  Node
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import CustomNode from './CustomNode';
import { CustomEdge } from './CustomEdge';
import { v4 as uuidv4 } from 'uuid';

interface WorkflowSpaceProps {
  activities?: DynamicActivityDefinition[];
  links?: any[];
  onEdit?: (index: number) => void;
  onRemove?: (activity: DynamicActivityDefinition, index: number) => void;
  onActivityMove?: (nodeId: string, position: { x: number; y: number }) => void;
  onActivityDrop?: (activity: any, position: { x: number; y: number }) => void;
  onLinksChange?: (links: { from: string; to: string }[], nodes: any[]) => void;
}

const nodeTypes = { custom: CustomNode };

const WorkflowSpace: React.FC<WorkflowSpaceProps> = ({
  activities = [],
  links = [],
  onEdit,
  onRemove,
  onActivityMove,
  onActivityDrop,
  onLinksChange,
}) => {

  const [nodes, setNodes, rawOnNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  const edgeTypes = useMemo(() => ({
    'custom-edge': (props: any) => (
      <CustomEdge {...props} onDeleteEdge={(id: string) => {
        setEdges((eds: Edge[]) => eds.filter((e: Edge) => e.id !== id));
      }} />
    ),
  }), [setEdges]);

  useEffect(() => {
    const newNodes = activities.map((activity, index) => {
      const nodeId = activity?.nodeId || uuidv4();
      const existingNode = nodes.find(n => n.id === nodeId);

      // Create a unique key based on activity data for force re-render when needed
      const activityKey = `${activity.type}-${JSON.stringify(activity.parameters)}-${activity.name}-${Date.now()}`;
      return {
        id: nodeId,
        type: 'custom',
        position: existingNode?.position || activity.position || { x: 100 + (index * 200), y: 200 },
        data: {
          label: nodeId,
          ...activity,
          lastUpdated: Date.now(),
          key: activityKey,
          onEdit: () => onEdit?.(index),
          onRemove: () => {
            setEdges((eds) => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
            onRemove?.(activity, index);
          },
        },
      };
    });
    setNodes(newNodes);
  }, [activities, onEdit, onRemove]);

  useEffect(() => {
    if (links.length > 0) {
      setEdges(links.map(link => ({
        id: `edge-${link.from}-${link.to}`,
        source: link.from,
        target: link.to,
        type: 'custom-edge',
      })));
    }
  }, [activities]);

  // Custom onNodesChange to detect position changes
  const onNodesChange = useCallback((changes: any) => {
    rawOnNodesChange(changes);
    if (onActivityMove) {
      changes.forEach((change: any) => {
        if (change.type === 'position' && change.position) {
          onActivityMove(change.id, change.position);
        }
      });
    }
  }, [rawOnNodesChange, onActivityMove]);

  const onConnect = useCallback(
    (connection: any) => {
      const edge = { ...connection, type: 'custom-edge' };
      setEdges((eds) => addEdge(edge, eds));
    },
    [setEdges],
  );

  // Notify parent on node move or edge change
  useEffect(() => {
    if (onLinksChange) {
      const links = edges.map(e => ({ from: e.source, to: e.target }));
      onLinksChange(links, nodes);
    }
  }, [edges, onLinksChange]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
    >
      <Controls />
      <Background />
    </ReactFlow>
  );
};

export default WorkflowSpace;
